import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, Building2, CheckCircle2, XCircle, Clock, Link as LinkIcon } from "lucide-react";
import api from "../lib/api";
import { PageHeader } from "../components/AppLayout";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { FileUploader, FileChip } from "../components/module-widgets";
import { timeAgo } from "../components/shared";

const STATUS = {
  new: { label: "Awaiting Action", cls: "bg-blue-500/10 text-blue-600 dark:text-blue-400", icon: Clock },
  quote_sent: { label: "Quote Sent — Awaiting Client Approval", cls: "bg-amber-500/10 text-amber-600 dark:text-amber-400", icon: Send },
  approved: { label: "Approved by Client", cls: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400", icon: CheckCircle2 },
  declined: { label: "Declined by Client", cls: "bg-red-500/10 text-red-600 dark:text-red-400", icon: XCircle },
};

export default function ServiceRequests() {
  const [leads, setLeads] = useState(null);
  const [quote, setQuote] = useState(null);
  const [form, setForm] = useState({ quote_amount: "", quote_note: "", attach: "none", quote_file: null, quote_link: "" });

  const load = () => api.get("/admin/leads").then((r) => setLeads(r.data));
  useEffect(() => { load(); }, []);

  const openQuote = (l) => {
    setForm({
      quote_amount: l.quote_amount || "", quote_note: l.quote_note || "",
      attach: l.quote_file ? "file" : l.quote_link ? "link" : "none",
      quote_file: l.quote_file || null, quote_link: l.quote_link || "",
    });
    setQuote(l);
  };

  const sendQuote = async () => {
    try {
      const payload = {
        quote_amount: form.quote_amount, quote_note: form.quote_note,
        quote_file: form.attach === "file" ? form.quote_file : null,
        quote_link: form.attach === "link" ? form.quote_link : null,
      };
      await api.patch(`/admin/leads/${quote.id}/quote`, payload);
      toast.success("Quote sent — awaiting client approval.");
      setQuote(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send quote"); }
  };

  if (!leads) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--accent))]" /></div>;

  const pending = leads.filter((l) => l.status === "new");
  const active = leads.filter((l) => l.status !== "new");

  const Row = (l) => {
    const s = STATUS[l.status] || STATUS.new;
    const Icon = s.icon;
    return (
      <div key={l.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 px-5 py-4 last:border-0" data-testid={`lead-row-${l.id}`}>
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-medium">{l.service}<Badge variant="secondary" className="rounded-full text-[10px]">{l.provider}</Badge></div>
          <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground"><Building2 className="h-3 w-3" />{l.org_name} · {l.requested_by} · {timeAgo(l.created_at)}</div>
          {l.notes && <div className="mt-1 max-w-xl text-xs text-muted-foreground">“{l.notes}”</div>}
          {l.quote_amount && <div className="mt-1 text-xs">Quote: <span className="font-semibold text-foreground">{l.quote_amount}</span>{l.quote_note && <span className="text-muted-foreground"> — {l.quote_note}</span>}</div>}
          {l.quote_file && <div className="mt-1.5"><FileChip file={l.quote_file} /></div>}
          {l.quote_link && <a href={l.quote_link} target="_blank" rel="noreferrer" className="mt-1.5 inline-flex items-center gap-1 text-xs text-[hsl(var(--accent))] hover:underline" data-testid={`lead-quote-link-${l.id}`}><LinkIcon className="h-3 w-3" /> View quote link</a>}
          {l.status === "approved" && (l.po_file || l.po_note) && (
            <div className="mt-2 rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Client PO / details</div>
              {l.po_note && <div className="mt-0.5 text-xs text-muted-foreground">{l.po_note}</div>}
              {l.po_file && <div className="mt-1.5"><FileChip file={l.po_file} /></div>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Badge className={`rounded-full ${s.cls}`}><Icon className="mr-1 h-3 w-3" />{s.label}</Badge>
          {(l.status === "new" || l.status === "quote_sent" || l.status === "declined") && (
            <Button size="sm" className="rounded-full text-xs" onClick={() => openQuote(l)} data-testid={`quote-btn-${l.id}`}>
              <Send className="mr-1 h-3 w-3" /> {l.status === "new" ? "Send Quote" : "Update Quote"}
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      <PageHeader title="Service Requests" subtitle="Client marketplace requests land here. Action them by sending a quote for client approval." />

      <div className="mb-6">
        <h2 className="mb-2 font-heading text-sm font-semibold uppercase tracking-wider text-muted-foreground">Awaiting your action <span className="text-[hsl(var(--accent))]">({pending.length})</span></h2>
        <Card className="rounded-2xl border-border" data-testid="pending-leads">
          {pending.length ? pending.map(Row) : <div className="py-8 text-center text-sm text-muted-foreground">No new requests to action.</div>}
        </Card>
      </div>

      <div>
        <h2 className="mb-2 font-heading text-sm font-semibold uppercase tracking-wider text-muted-foreground">Quoted & closed</h2>
        <Card className="rounded-2xl border-border" data-testid="active-leads">
          {active.length ? active.map(Row) : <div className="py-8 text-center text-sm text-muted-foreground">Nothing here yet.</div>}
        </Card>
      </div>

      <Dialog open={!!quote} onOpenChange={(o) => !o && setQuote(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Send Quote — {quote?.service}</DialogTitle></DialogHeader>
          {quote && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">For <span className="font-medium text-foreground">{quote.org_name}</span> · {quote.provider}. The client will be asked to approve this quote.</p>
              <div className="space-y-1.5"><Label>Quote amount</Label><Input value={form.quote_amount} onChange={(e) => setForm({ ...form, quote_amount: e.target.value })} placeholder="e.g. €2,500 + VAT" data-testid="quote-amount" /></div>
              <div className="space-y-1.5"><Label>Details / scope</Label><Textarea rows={4} value={form.quote_note} onChange={(e) => setForm({ ...form, quote_note: e.target.value })} placeholder="What's included, timeline, terms…" data-testid="quote-note" /></div>
              <div className="space-y-2">
                <Label>Attach quote document</Label>
                <Tabs value={form.attach} onValueChange={(v) => setForm({ ...form, attach: v })}>
                  <TabsList className="grid w-full grid-cols-3 rounded-full">
                    <TabsTrigger value="none" className="rounded-full" data-testid="quote-attach-none">None</TabsTrigger>
                    <TabsTrigger value="file" className="rounded-full" data-testid="quote-attach-file">PDF / File</TabsTrigger>
                    <TabsTrigger value="link" className="rounded-full" data-testid="quote-attach-link">Link</TabsTrigger>
                  </TabsList>
                </Tabs>
                {form.attach === "file" && (
                  <div className="space-y-2">
                    {form.quote_file && <FileChip file={form.quote_file} />}
                    <FileUploader label={form.quote_file ? "Replace file" : "Upload quote PDF"} testid="quote-upload" onUploaded={(f) => setForm({ ...form, quote_file: f })} />
                  </div>
                )}
                {form.attach === "link" && (
                  <Input value={form.quote_link} onChange={(e) => setForm({ ...form, quote_link: e.target.value })} placeholder="https://…" data-testid="quote-link" />
                )}
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={sendQuote} className="rounded-full" data-testid="send-quote-btn"><Send className="mr-2 h-4 w-4" /> Send Quote</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
